import { useRef, type ReactNode } from "react";

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/shared/components/primitives/combobox";
import useTranslations from "@/shared/lang/useTranslations";

import { buildComboboxFilter } from "./colocaCombobox.shared";

/** A single selectable option. */
export type ColocaComboboxOption = {
  /** Value reported through `onValueChange`. */
  value: string;
  /** Text shown in the dropdown, in the input after selection, and used for filtering. */
  label: string;
  disabled?: boolean;
};

interface ColocaComboboxProps {
  /** Selected option value; `null` when none is selected. */
  value: string | null;
  onValueChange: (value: string | null) => void;
  options: ColocaComboboxOption[];
  placeholder?: string;
  /** Text rendered when no options match the query. Defaults to the global "not found" message. */
  emptyMessage?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  "aria-label"?: string;
  /**
   * Custom render for each dropdown row (e.g. a right-aligned badge). The selected-value display still uses
   * the plain `label` string. Mirrors `ColocaComboboxField`'s `renderItem`.
   */
  renderOption?: (option: ColocaComboboxOption) => ReactNode;
  /**
   * Override the default case-insensitive substring filter over `label`. Receives the option's `value` and
   * the current query; return `true` to show it. Mirrors `ColocaComboboxField`'s `filter` — use it when the
   * option is findable by something the label does not spell out (an id, a code, an alias).
   *
   * Pass `null` when `options` are already the result of a server-side search: filtering them again in the
   * browser can only hide rows the server decided to return.
   */
  filter?: ((itemValue: string, query: string) => boolean) | null;
  /**
   * Fires with the query the USER typed (or cleared), for a combobox backed by a server-side search. Pair it
   * with `filter={null}` and feed the results back through `options`.
   */
  onSearchChange?: (query: string) => void;
}

/**
 * Standalone searchable single-select combobox. Use when you need a filterable dropdown outside a React Hook
 * Form context. For form-bound usage prefer `ColocaComboboxField`.
 */
export function ColocaCombobox({
  value,
  onValueChange,
  options,
  placeholder,
  emptyMessage,
  disabled,
  id,
  className,
  renderOption,
  filter,
  onSearchChange,
  ...rest
}: ColocaComboboxProps) {
  const { t } = useTranslations("global");
  // Lets the dropdown hide itself while the field is scrolled out of view (e.g. inside a scrollable dialog),
  // instead of floating over whatever is on top. Positioning is untouched.
  const fieldRef = useRef<HTMLInputElement | null>(null);

  return (
    <Combobox
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      items={options}
      filter={buildComboboxFilter(filter)}
      // base-ui reports every input-value change here, including the ones it writes itself: the selected
      // item's label on press, and a re-sync whenever `items` changes identity. Forwarding those closes a
      // render loop through whatever the caller derives `options` from, which is what took down
      // /app/payments-config/rails. Only a real edit is a query.
      onInputValueChange={
        onSearchChange
          ? (inputValue, eventDetails) => {
              if (eventDetails.reason !== "input-change" && eventDetails.reason !== "input-clear") return;
              onSearchChange(inputValue);
            }
          : undefined
      }
      itemToStringLabel={(v: string) => options.find((o) => o.value === v)?.label ?? v}
    >
      <ComboboxInput
        ref={fieldRef}
        id={id}
        aria-label={rest["aria-label"]}
        placeholder={placeholder}
        showClear
        showTrigger
        disabled={disabled}
        className={className}
      />
      <ComboboxContent clipAnchor={fieldRef}>
        <ComboboxList>
          <ComboboxCollection>
            {(item: ColocaComboboxOption) => (
              <ComboboxItem key={item.value} value={item.value} disabled={item.disabled}>
                {renderOption ? renderOption(item) : item.label}
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
        <ComboboxEmpty>{emptyMessage ?? t("not_found_message")}</ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}
